import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import KeyValueCard from "@/components/cards/KeyValueCard";
import NoDataAvailable from "@/components/cards/NoDataAvailable";
import { SettingKeyValueRowsSkeleton } from "@/components/skeletons";
import { fetchLogoutRedirect, fetchSetting, fetchWebhook, getConnector, patchConfiguration, saveLogoutRedirect, saveWebhook } from "@/services/setting.service";
import { BUTTONS, GLOBAL_ORGANIZATION, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { ALL_SIGNATURE_STYLES } from "@/utils/constants/constants";
import { LOGOUT_REDIRECT_UPDATE_SUCCESSFULLY, NO_DOMAIN_SPECIFIED, SETTING_SUB_TEXT, SOMETHING_WENT_WRONG, TIMEZONE_UPDATE_SUCCESSFULLY, WEBHOOK_UPDATE_SUCCESSFULLY } from "@/utils/constants/messages";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { useIsSuperAdmin } from "@/utils/hooks/useIsSuperAdmin";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import { getCurrentOrganization, setAllOrganizations } from "@/utils/functions/OrganizationFunctions";
import { DIRECTORY_SERVICE } from "@/utils/constants/appConstants/DirectoryConstants";
import { SettingCheckboxCard } from "@/components/cards/SettingCheckboxCard";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import TimezoneSelect from "react-timezone-select";
import { REACT_SELECT_MENU_PORTAL_PROPS } from "@/lib/reactSelectPortal";
import { getLoggedInUser } from "@/services/user.service";

type SettingSectionProps = {
    title: string;
    id?: string;
    description?: string;
    descriptionId?: string;
    children: React.ReactNode;
};

const SettingSection = ({ title, id, description, descriptionId, children }: SettingSectionProps) => (
    <section className="gs-setting-section">
        <div className="gs-setting-card">
            <div className="gs-setting-card__header">
                <h4 className="gs-setting-section__title" id={id}>
                    {title}
                </h4>
                {description ? (
                    <p className="gs-setting-section__desc" id={descriptionId}>
                        {description}
                    </p>
                ) : null}
            </div>
            <div className="gs-setting-card__body">{children}</div>
        </div>
    </section>
);

type SettingActionRowProps = {
    label: string;
    id?: string;
    children: React.ReactNode;
};

const SettingActionRow = ({ label, id, children }: SettingActionRowProps) => (
    <div className="gs-setting-action-row">
        <h6 className="gs-setting-action-row__label" id={id}>
            {label}
        </h6>
        <div className="gs-setting-action-row__controls">{children}</div>
    </div>
);

function License() {
    const [settings, setSettings] = useState<any>({});
    const [settingLoading, setSettingLoading] = useState(false);
    const [webhookLoading, setWebhookLoading] = useState(false);
    const [logoutRedirectLoading, setLogoutRedirectLoading] = useState(false);
    const [signatureCheckBoxDisabled, setSignatureCheckBoxDisabled] = useState(false);
    const [webHook, setWebHook] = useState("");
    const [logoutRedirect, setLogoutRedirect] = useState("");
    const isViewIntegrationPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalViewIntegrationSetting);
    const isSuperAdminUser = useIsSuperAdmin();
    const [selectedTimezone, setSelectedTimezone] = useState<any>();
    const currentOrganization = getCurrentOrganization();

    useEffect(() => setSignatureCheckBoxDisabled(settings?.configurations?.recipient_signature_options_allowed?.length === 1), [settings]);

    useEffect(() => {
        setSettingLoading(true);
        fetchSetting(
            (response: any) => {
                setSettings(response);
                setSettingLoading(false);
                localStorage.setItem(
                    "isDocumentSearchEnabled",
                    response?.configurations?.is_document_search_enabled ?? false
                );
                if(response.configurations.timezone) {
                    setSelectedTimezone({
                        value: response?.configurations?.timezone?.value,
                        label: response?.configurations?.timezone?.label,
                        offset: response?.configurations?.timezone?.offset,
                        abbrev: response?.configurations?.timezone?.abbrev,
                        altName: response?.configurations?.timezone?.alt_name,
                    });
                }
            },
            (error: any) => {
                console.log(error);
                setSettingLoading(false);
            }
        );
        getConnector({ type: "azure" }, () => {}, (error: any) => console.log(error));
        setWebhookLoading(true);
        setLogoutRedirectLoading(true);
        fetchLogoutRedirect(
            (response: any) => {
                setLogoutRedirect(response?.data);
                setLogoutRedirectLoading(false);
            },
            (err: any) => {
                console.log(err);
                setLogoutRedirectLoading(false);
            }
        );
        fetchWebhook(
            (response: any) => {
                setWebHook(response?.data.endpoint);
                setWebhookLoading(false);
            },
            (err: any) => {
                console.log(err);
                setWebhookLoading(false);
            }
        );
    }, []);

    const onChangeConfiguration = async (e: React.ChangeEvent<HTMLInputElement>, checked: boolean | undefined) => {
        const newValue = checked !== undefined ? checked : !e.target.checked;
        if (e.target.name === "is_document_search_enabled" && !isSuperAdminUser) {
            return;
        }
        if(((e.target.name === 'is_recipient_domain_restricted' && newValue === true) || (e.target.name === 'is_signup_domain_restricted' && newValue === true)) && (!settings.configurations.allowed_domains || settings.configurations.allowed_domains.length === 0)){
            errorToasterMessage(NO_DOMAIN_SPECIFIED)
            return
        }
        setSettings({ ...settings, configurations: { ...settings.configurations, [e.target.name]: newValue } });
        const result = await patchConfiguration({ [e.target.name]: newValue });
        if (e.target.name === "is_signup_domain_restricted") {
            getLoggedInUser((res: any) => setAllOrganizations(res.organizations), (err: any) => console.log(err));
        }
        localStorage.setItem('isSharedAccess', result.data.configurations.is_shared_access_enabled)
        if (e.target.name === "is_document_search_enabled") {
            localStorage.setItem('isDocumentSearchEnabled', result.data.configurations.is_document_search_enabled)
        }
    };

    const onChangeStyle = async (e: { target: { id: any; checked: any } }) => {
        const new_signature_styles = ALL_SIGNATURE_STYLES.map((obj: { key: any }) => {
            if (obj.key === e.target.id && e.target.checked) return obj.key;
            if (obj.key !== e.target.id) {
                return settings?.configurations.recipient_signature_options_allowed.some((conf_key: any) => obj.key === conf_key)
                    ? obj.key
                    : null;
            }
            return null;
        }).filter((obj: any) => !!obj);
        setSettings({
            ...settings,
            configurations: { ...settings.configurations, recipient_signature_options_allowed: new_signature_styles },
        });
        if (new_signature_styles.length > 0) await patchConfiguration({ recipient_signature_options_allowed: new_signature_styles });
    };

    const onSaveTimeZone = async () => {
        setSettings({ ...settings, configurations: { ...settings.configurations, timezone: selectedTimezone } });
        await patchConfiguration({
            timezone: {
                value: selectedTimezone.value,
                label: selectedTimezone.label,
                offset: selectedTimezone.offset,
                abbrev: selectedTimezone.abbrev,
                alt_name: selectedTimezone.altName,
            },
        })
            .then((res) => {
                if (res.status === 200) successToasterMessage(TIMEZONE_UPDATE_SUCCESSFULLY);
            })
            .catch(console.error);
    };

    const onSaveWebHook = async () => {
        await saveWebhook({ endpoint: webHook });
        successToasterMessage(WEBHOOK_UPDATE_SUCCESSFULLY);
    };

    const onSaveLogoutRedirect = async () => {
        await saveLogoutRedirect({ logout_redirect_url: logoutRedirect });
        successToasterMessage(LOGOUT_REDIRECT_UPDATE_SUCCESSFULLY);
    };

    return (
        <div className="gs-setting-page">
            <SettingSection
                title={HEADINGS.LICENSE_DETAILS}
                id="setting-license-title"
                description={SETTING_SUB_TEXT}
                descriptionId="setting-license-sub-heading"
            >
                    {settings?.organization?.licenses.length > 0 && !settingLoading ? (
                        <>
                            <KeyValueCard id="setting-organization-name" label="Organization Name" value={settings?.organization?.name} />
                            {settings?.organization?.licenses?.map((license: any, key: number) => (
                                <div key={key}>
                                    <KeyValueCard id="setting-license-key" label="License Key" value={license?.license_key} />
                                    <KeyValueCard id="setting-license-grace-period" label="License Grace Period" value={`${license?.grace_period} days`} />
                                    <KeyValueCard
                                        id="setting-license-expiry-at"
                                        className="text-danger"
                                        label="License Expiry Date"
                                        value={license?.expiry_at.split("T")[0].split("-").reverse().join("-")}
                                    />
                                </div>
                            ))}
                        </>
                    ) : settingLoading ? (
                        <SettingKeyValueRowsSkeleton id="setting-license" rowCount={3} />
                    ) : (
                        <NoDataAvailable>
                            <h4 className="text-center" id="setting-license-option-error-message">
                                {SOMETHING_WENT_WRONG}
                            </h4>
                        </NoDataAvailable>
                    )}
            </SettingSection>

            <SettingSection title={HEADINGS.DEVELOPER_OPTIONS} id="setting-developer-options-heading">
                    {settings?.organization?.identifier && !settingLoading ? (
                        <>
                            <KeyValueCard
                                id="setting-developer-options-organization-identifier"
                                label="Organization Identifier"
                                value={settings?.organization?.identifier}
                            />
                            {settings?.api_keys?.map((key: { environment: string; value: any }, i: number) => (
                                <KeyValueCard
                                    key={i}
                                    id="setting-developer-options-api-key"
                                    label={`${key?.environment?.charAt(0).toUpperCase() + key?.environment?.slice(1)} API Key`}
                                    value={key?.value as string}
                                />
                            ))}
                            <SettingActionRow label={HEADINGS.WEBHOOK} id="setting-webhook-label">
                                <Input
                                    type="text"
                                    value={webHook}
                                    onChange={(e) => setWebHook(e.target.value)}
                                    className="gs-setting-action-row__input"
                                    id="setting-webhook-input"
                                />
                                <Button
                                    id="setting-webhook-save-btn"
                                    className="gs-setting-action-row__btn"
                                    onClick={onSaveWebHook}
                                    disabled={webhookLoading}
                                >
                                    {webhookLoading ? <Spinner size="sm" className="text-white" /> : BUTTONS.SAVE}
                                </Button>
                            </SettingActionRow>
                            {currentOrganization.name !== GLOBAL_ORGANIZATION && (
                                <SettingActionRow label={HEADINGS.LOGOUT_REDIRECT} id="setting-redirect-label">
                                    <Input
                                        type="text"
                                        value={logoutRedirect}
                                        onChange={(e) => setLogoutRedirect(e.target.value)}
                                        className="gs-setting-action-row__input"
                                        id="setting-logout-redirect"
                                    />
                                    <Button
                                        id="setting-logout-redirect-save-btn"
                                        className="gs-setting-action-row__btn"
                                        onClick={onSaveLogoutRedirect}
                                        disabled={logoutRedirectLoading}
                                    >
                                        {logoutRedirectLoading ? <Spinner size="sm" className="text-white" /> : BUTTONS.SAVE}
                                    </Button>
                                </SettingActionRow>
                            )}
                        </>
                    ) : settingLoading ? (
                        <SettingKeyValueRowsSkeleton
                            id="setting-developer-option"
                            rowCount={4}
                            labelClassName="h-4 w-48"
                            valueClassName="h-4 w-64"
                        />
                    ) : (
                        <NoDataAvailable>
                            <h4 className="text-center" id="setting-developer-option-error-message">
                                {SOMETHING_WENT_WRONG}
                            </h4>
                        </NoDataAvailable>
                    )}
            </SettingSection>

            <SettingSection title="Signature" id="setting-signature-option">
                    <SettingCheckboxCard
                        onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                        id="is_lock_recipient_name_enabled"
                        name="is_lock_recipient_name_enabled"
                        checked={settings?.configurations?.is_lock_recipient_name_enabled}
                    >
                        <p id="setting_is_lock_recipient_name_enabled_label">{LABELS.LOCK_RECIPIENT_NAME}</p>
                    </SettingCheckboxCard>
                    <SettingCheckboxCard
                        onChange={(e) => onChangeConfiguration(e, !e.target.checked)}
                        id="is_frame_attached"
                        name="is_frame_attached"
                        checked={!settings?.configurations?.is_frame_attached}
                    >
                        <p id="setting_is_frame_attached_label">{LABELS.HIDE_SIGNATURE_FRAME}</p>
                    </SettingCheckboxCard>
            </SettingSection>

            <SettingSection title="Allowed Style Options" id="setting-allowed-style-option">
                    {ALL_SIGNATURE_STYLES.map((style: any, index: number) => (
                        <SettingCheckboxCard
                            key={style.key}
                            index={style.key}
                            disabled={signatureCheckBoxDisabled && settings?.configurations?.recipient_signature_options_allowed[0] === style.key}
                            onChange={onChangeStyle}
                            name={style.name as string}
                            id={style.key as string}
                            checked={settings?.configurations?.recipient_signature_options_allowed?.some(
                                (style_config: any) => style_config === style.key
                            )}
                        >
                            <p id={`setting-signature-style-${index}`}>{style.name}</p>
                        </SettingCheckboxCard>
                    ))}
            </SettingSection>

            {currentOrganization.name !== GLOBAL_ORGANIZATION && (
                <SettingSection title={HEADINGS.REGIONAL} id="setting-regional-option">
                        <SettingActionRow label={HEADINGS.TIMEZONE} id="setting-timezone-label">
                            <div className="gs-setting-timezone">
                                <TimezoneSelect
                                    displayValue="UTC"
                                    value={selectedTimezone}
                                    onChange={setSelectedTimezone}
                                    className="gs-setting-timezone-select"
                                    classNamePrefix="react-select"
                                    {...REACT_SELECT_MENU_PORTAL_PROPS}
                                />
                            </div>
                            <Button id="setting-timezone-save-btn" className="gs-setting-action-row__btn" onClick={onSaveTimeZone}>
                                {BUTTONS.SAVE}
                            </Button>
                        </SettingActionRow>
                </SettingSection>
            )}

            <SettingSection title="Access Control" id="setting-access-control-option">
                    <SettingCheckboxCard
                        onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                        id="is_shared_access_enabled"
                        name="is_shared_access_enabled"
                        checked={settings?.configurations?.is_shared_access_enabled}
                    >
                        <p id="setting_is_shared_access_enabled_label">{LABELS.SHARED_ACCESS_ENABLE}</p>
                    </SettingCheckboxCard>
                    <SettingCheckboxCard
                        onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                        id="is_decline_to_sign_enabled"
                        name="is_decline_to_sign_enabled"
                        checked={settings?.configurations?.is_decline_to_sign_enabled}
                    >
                        <p id="setting_is_decline_to_sign_enabled_label">{LABELS.DECLINE_TO_SIGN}</p>
                    </SettingCheckboxCard>
                    <SettingCheckboxCard
                        onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                        id="is_correct_per_recipient_allowed"
                        name="is_correct_per_recipient_allowed"
                        checked={settings?.configurations?.is_correct_per_recipient_allowed}
                    >
                        <p id="setting_is_correct_per_recipient_allowed_label">{LABELS.CORRECT_ENVELOPE_BY_RECIPIENTS}</p>
                    </SettingCheckboxCard>
                    {currentOrganization.name !== GLOBAL_ORGANIZATION && (
                        <SettingCheckboxCard
                            onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                            id="is_recipient_domain_restricted"
                            name="is_recipient_domain_restricted"
                            checked={settings?.configurations?.is_recipient_domain_restricted}
                        >
                            <p id="setting_is_recipient_domain_restricted_label">{LABELS.DOMAIN_RESTRICTION_FOR_RECIPIENTS}</p>
                        </SettingCheckboxCard>
                    )}
                    {currentOrganization.name !== GLOBAL_ORGANIZATION && (
                        <SettingCheckboxCard
                            onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                            id="is_signup_domain_restricted"
                            name="is_signup_domain_restricted"
                            checked={settings?.configurations?.is_signup_domain_restricted}
                        >
                            <p id="setting_is_signup_domain_restricted_label">{LABELS.DOMAIN_RESTRICTION_FOR_SIGNUP}</p>
                        </SettingCheckboxCard>
                    )}
                    {currentOrganization.name !== GLOBAL_ORGANIZATION && settings.configurations?.directory_connector_type && (
                        <SettingCheckboxCard
                            onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                            id="is_directory_service_enabled"
                            name="is_directory_service_enabled"
                            checked={settings?.configurations?.is_directory_service_enabled}
                        >
                            <p id="setting_is_directory_service_enabled_label">{LABELS.DIRECTORY_SERVICE_ENABLE}</p>
                        </SettingCheckboxCard>
                    )}
                    {currentOrganization.name !== GLOBAL_ORGANIZATION && settings?.configurations?.is_directory_service_enabled && (
                        <SettingCheckboxCard
                            onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                            id="is_restrict_login_with_sso"
                            name="is_restrict_login_with_sso"
                            checked={settings?.configurations?.is_restrict_login_with_sso}
                        >
                            <p id="setting_is_restrict_login_with_sso_label">{LABELS.PASSWORD_ENABLED}</p>
                        </SettingCheckboxCard>
                    )}
                    {currentOrganization.name !== GLOBAL_ORGANIZATION && (
                        <SettingCheckboxCard
                            onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                            id="is_unique_field_validation_enabled"
                            name="is_unique_field_validation_enabled"
                            checked={settings?.configurations?.is_unique_field_validation_enabled}
                        >
                            <p id="setting_is_unique_field_validation_enabled_label">{LABELS.UNIQUE_FIELD_VALIDATION}</p>
                        </SettingCheckboxCard>
                    )}
                    {currentOrganization.name !== GLOBAL_ORGANIZATION && (
                        <SettingCheckboxCard
                            onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                            id="is_sharing_allow"
                            name="is_sharing_allow"
                            checked={settings?.configurations?.is_sharing_allow}
                        >
                            <p id="is_sharing_allow">{LABELS.ALLOW_SHARING}</p>
                        </SettingCheckboxCard>
                    )}
                    <SettingCheckboxCard
                        onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                        id="is_supplemental_documents_allowed"
                        name="is_supplemental_documents_allowed"
                        checked={settings?.configurations?.is_supplemental_documents_allowed}
                    >
                        <p id="setting_is_supplemental_documents_allowed">{LABELS.SUPPLEMENTAL_DOCUMENT_ALLOWED}</p>
                    </SettingCheckboxCard>
                    <SettingCheckboxCard
                        onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                        id="is_recipient_reassignment_enabled"
                        name="is_recipient_reassignment_enabled"
                        checked={settings?.configurations?.is_recipient_reassignment_enabled}
                    >
                        <p id="setting_is_recipient_reassignment_enabled_label">{LABELS.RECIPIENT_REASSIGNMENT_ENABLED}</p>
                    </SettingCheckboxCard>
                    {currentOrganization.name !== GLOBAL_ORGANIZATION && (
                        <SettingCheckboxCard
                            onChange={(e) => onChangeConfiguration(e, e.target.checked)}
                            id="is_document_search_enabled"
                            name="is_document_search_enabled"
                            checked={settings?.configurations?.is_document_search_enabled}
                            disabled={!isSuperAdminUser}
                        >
                            <p id="is_document_search_enabled">{LABELS.DOCUMENT_SEARCH_ENABLED}</p>
                        </SettingCheckboxCard>
                    )}
            </SettingSection>

            <SettingSection title={HEADINGS.INTEGRATION} id="setting-integration-option">
                    <SettingActionRow label={HEADINGS.SHAREPOINT} id="setting-integration-sharepoint-option">
                        {isViewIntegrationPermitted && (
                            <Link to="/register/azure" className="gs-setting-manage-link" id="setting-integration-manage-option">
                                {LABELS.MANAGE}
                            </Link>
                        )}
                    </SettingActionRow>
                    {currentOrganization.name !== GLOBAL_ORGANIZATION && (
                        <SettingActionRow label={DIRECTORY_SERVICE}>
                            <Link to="/directory" className="gs-setting-manage-link">
                                {LABELS.MANAGE}
                            </Link>
                        </SettingActionRow>
                    )}
            </SettingSection>
        </div>
    );
}

export default License;
